
import Vue, { PropType } from 'vue'
import api from '@root/src/api/api'
import Opz2Icon from './Opz2Icon.vue'
import Opz2DaysPane from './Opz2DaysPane.vue'
import Opz2OpzPane from './Opz2OpzPane.vue'
import Opz2CrossPane from './Opz2CrossPane.vue'
import Opz2KpiPane from './Opz2KpiPane.vue'
import Opz2SettingsPane from './Opz2SettingsPane.vue'
import Opz2ClientDrawer from './Opz2ClientDrawer.vue'
import {
    CrossFlag,
    EMPTY_REPORT,
    Report,
    StaffFlag,
    adminLabel,
    filterApplied,
    longDate,
    periodLabel,
    presetRange,
} from './model'

type Tab = 'days' | 'opz' | 'cross' | 'kpi' | 'settings'

const today = () => {
    const now = new Date()
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

export default Vue.extend({
    name: 'Opz2Page',
    components: {
        Opz2Icon,
        Opz2DaysPane,
        Opz2OpzPane,
        Opz2CrossPane,
        Opz2KpiPane,
        Opz2SettingsPane,
        Opz2ClientDrawer,
    },
    props: {
        projectId: { type: String, required: true },
        projectName: { type: String, default: '' },
        crmLabel: { type: String, default: 'YCLIENTS' },
        /** филиалы сети: [{ id, name }] */
        filialOptions: { type: Array as PropType<Array<{ id: string; name: string }>>, default: () => [] },
        /** псевдосеть: выбор одного проекта */
        pseudoNetworkProjects: {
            type: Array as PropType<Array<{ id: string; name: string }>>,
            default: () => [],
        },
        selectedProjectId: { type: String, default: '' },
    },
    data() {
        const range = presetRange('previous', today())
        return {
            tab: 'days' as Tab,
            from: range.from,
            to: range.to,
            draft: { ...range },
            defaultRange: range,
            committed: { ...range },
            admin: 'all',
            crossFlag: 'all' as CrossFlag,
            staffFlag: 'all' as StaffFlag,
            /** администраторы из kpi_opz без фильтра — список не сужается при выборе */
            adminOptions: [] as Array<{ id: string; name: string }>,
            clientId: null as string | null,
            filial: 'all',
            periodOpen: false,
            helpOpen: false,
            periodError: '',
            report: EMPTY_REPORT as Report,
            loaded: false,
            loading: false,
            loadError: false,
            status: '' as '' | 'loading' | 'success',
            statusTimer: 0 as any,
            requestId: 0,
            updatedAt: null as Date | null,
            settingsMounted: false,
            presets: [
                { id: 'week', label: 'Текущая неделя' },
                { id: 'month', label: 'Текущий месяц' },
                { id: 'previous', label: 'Прошлый месяц' },
                { id: 'quarter', label: 'Последние 3 месяца' },
            ] as Array<{ id: 'week' | 'month' | 'previous' | 'quarter'; label: string }>,
            tabs: [
                { id: 'days', label: 'По дням', icon: 'calendar' },
                { id: 'opz', label: 'ОПЗ 2.0', icon: 'swap' },
                { id: 'cross', label: 'Кросс-продажи', icon: 'cross' },
                { id: 'kpi', label: 'KPI администраторов', icon: 'chart' },
                { id: 'settings', label: 'Настройка KPI', icon: 'settings' },
            ] as Array<{ id: Tab; label: string; icon: string }>,
        }
    },
    computed: {
        periodText(): string {
            return periodLabel(this.from, this.to)
        },
        committedPeriodText(): string {
            return periodLabel(this.committed.from, this.committed.to)
        },
        adminText(): string {
            if (this.admin === 'all') return 'Все администраторы'
            return this.adminOptions.find(a => a.id === this.admin)?.name ?? this.admin
        },
        /** параметры отчёта для функции — те же уходят в карточку клиента */
        request(): Record<string, any> {
            return {
                projectId: this.projectId,
                dateFrom: this.from,
                dateTo: this.to,
                crossFlag: this.crossFlag,
                staffFlag: this.staffFlag,
                ...(this.admin !== 'all' ? { adminId: this.admin } : {}),
                ...(this.filialList ? { filialList: this.filialList } : {}),
            }
        },
        /** функция не подтвердила фильтр по администратору в applied_filter — данные не по нему */
        adminUnsupported(): boolean {
            return (
                this.admin !== 'all' &&
                this.report.adminId === this.admin &&
                !filterApplied(this.report.appliedFilter, 'opz_creating_user_id', this.admin)
            )
        },
        /** при неподтверждённом фильтре значения скрыты: прочерки вместо чужих цифр */
        shown(): Report {
            if (!this.adminUnsupported) return this.report
            return {
                ...EMPTY_REPORT,
                period: this.report.period,
                appliedFilter: this.report.appliedFilter,
                staffSelection: { ...EMPTY_REPORT.staffSelection, flag: this.report.staffSelection.flag },
                cross: {
                    ...EMPTY_REPORT.cross,
                    flag: this.report.cross.flag,
                },
                kpi: {
                    ...EMPTY_REPORT.kpi,
                    configured: this.report.kpi.configured,
                    schemeName: this.report.kpi.schemeName,
                },
                adminId: this.report.adminId,
                staff: this.report.staff,
                clients: this.report.clients,
                accounts: this.report.accounts,
                crmAdmins: this.report.crmAdmins,
            }
        },
        emptyLabel(): string {
            return this.adminUnsupported
                ? 'Фильтр по администратору расчёт ОПЗ пока не поддерживает'
                : 'В этом периоде нет визитов'
        },
        isDefault(): boolean {
            return (
                this.admin === 'all' &&
                this.crossFlag === 'all' &&
                this.staffFlag === 'all' &&
                this.filial === 'all' &&
                this.from === this.defaultRange.from &&
                this.to === this.defaultRange.to
            )
        },
        updatedText(): string {
            if (!this.updatedAt) return ''
            return `Обновлено в ${this.updatedAt.toLocaleTimeString('ru-RU', {
                hour: '2-digit',
                minute: '2-digit',
            })}`
        },
        filialList(): Record<string, any> | undefined {
            if (!this.filialOptions.length || this.filial === 'all') return undefined
            return this.filialOptions.reduce(
                (acc, item) =>
                    Object.assign(acc, {
                        [item.id]: { id: item.id, option: item.name, selected: item.id === this.filial },
                    }),
                {} as Record<string, any>
            )
        },
    },
    watch: {
        filial() {
            this.load()
        },
        projectId() {
            this.load()
        },
        tab(value: Tab) {
            // настройки грузятся только когда их открыли
            if (value === 'settings') this.settingsMounted = true
        },
        admin() {
            this.load()
        },
    },
    created() {
        this.load()
    },
    mounted() {
        document.addEventListener('click', this.onDocumentClick)
        document.addEventListener('keydown', this.onKeydown)
    },
    beforeDestroy() {
        this.requestId++
        clearTimeout(this.statusTimer)
        document.removeEventListener('click', this.onDocumentClick)
        document.removeEventListener('keydown', this.onKeydown)
    },
    methods: {
        longDate,
        async load() {
            const requestId = ++this.requestId
            clearTimeout(this.statusTimer)
            this.loading = true
            this.loadError = false
            this.status = 'loading'
            const { data, error } = await api.gcb2.getOpz2ReportV3(this.request as any)
            if (requestId !== this.requestId) return
            this.loading = false
            if (error || !data) {
                this.loadError = true
                this.status = ''
                return
            }
            this.report = data
            if (this.admin === 'all') {
                this.adminOptions = (data.kpi?.rows ?? [])
                    .map((row: any) => String(row.opz_creating_user_id ?? ''))
                    .filter(Boolean)
                    .map((id: string) => ({ id, name: adminLabel(data, id) }))
                    .sort((a: any, b: any) => a.name.localeCompare(b.name, 'ru'))
            }
            this.committed = { from: this.from, to: this.to }
            this.loaded = true
            this.updatedAt = new Date()
            this.flash()
        },
        flash() {
            clearTimeout(this.statusTimer)
            this.status = 'success'
            this.statusTimer = setTimeout(() => (this.status = ''), 3200)
        },
        applyPreset(id: 'week' | 'month' | 'previous' | 'quarter') {
            this.draft = presetRange(id, today())
            this.periodError = ''
        },
        applyPeriod() {
            if (!this.draft.from || !this.draft.to || this.draft.from > this.draft.to) {
                this.periodError = 'Выберите обе даты. Начало периода должно быть раньше его окончания.'
                return
            }
            this.periodError = ''
            this.from = this.draft.from
            this.to = this.draft.to
            this.periodOpen = false
            this.load()
        },
        resetFilters() {
            this.from = this.defaultRange.from
            this.to = this.defaultRange.to
            this.draft = { ...this.defaultRange }
            this.crossFlag = 'all'
            this.staffFlag = 'all'
            const adminChanged = this.admin !== 'all'
            this.admin = 'all'
            const filialChanged = this.filial !== 'all'
            this.filial = 'all'
            if (!filialChanged && !adminChanged) this.load()
        },
        /** Фильтры вкладок — параметры функции, поэтому запрашиваем отчёт заново. */
        setFilter(change: { crossFlag?: CrossFlag; staffFlag?: StaffFlag }) {
            Object.assign(this, change)
            this.load()
        },
        openClient(clientId: string) {
            this.clientId = clientId
        },
        onSettingsSaved({ firstSetup }: { firstSetup: boolean }) {
            if (firstSetup) this.tab = 'kpi'
            this.load()
        },
        onDocumentClick(event: MouseEvent) {
            const path = event.composedPath ? event.composedPath() : []
            if (this.periodOpen && !path.includes(this.$refs.periodDetails as Element))
                this.periodOpen = false
        },
        onKeydown(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                this.periodOpen = false
                this.helpOpen = false
            }
        },
        onTabsKeydown(event: KeyboardEvent) {
            const ids = this.tabs.map(t => t.id)
            const i = ids.indexOf(this.tab)
            let n: number
            if (event.key === 'ArrowRight') n = (i + 1) % ids.length
            else if (event.key === 'ArrowLeft') n = (i - 1 + ids.length) % ids.length
            else if (event.key === 'Home') n = 0
            else if (event.key === 'End') n = ids.length - 1
            else return
            event.preventDefault()
            this.tab = ids[n]
            this.$nextTick(() =>
                (document.getElementById(`opz2-tab-${ids[n]}`) as HTMLElement | null)?.focus()
            )
        },
    },
})
